/**
 * Unified fetch wrapper and API functions for the Worker API.
 *
 * Replaces both the old `fetchWithAuth` (inbox/conversation endpoints) and
 * `adminFetch` (admin endpoints) with a single `fetchApi` function.
 *
 * - Automatically attaches the Supabase access token as a Bearer header.
 * - Captures `x-request-id` from responses for debugging.
 * - Throws `ApiError` on non-2xx responses, including the request ID so
 *   operators can look up logs immediately.
 * - Surfaces 429 sync-cooldown responses as `SyncCooldownError` so the UI
 *   can show a countdown timer.
 */

import { getAccessToken } from "./supabase";
import { API_BASE_URL } from "./config";
import {
  ApiError,
  SyncCooldownError,
  type InboxResponse,
  type InboxParams,
  type ConversationResponse,
  type ConversationUpdateBody,
  type MessagesResponse,
  type MessagesParams,
  type MeResponse,
  type BusinessesResponse,
  type BusinessConnectionsResponse,
  type BusinessUpdateBody,
  type BusinessUpdateResponse,
  type CreateMailboxBody,
  type CreateMailboxResponse,
  type Draft,
  type SaveDraftResponse,
  type SaveDraftInput,
  type SendDraftResponse,
  type StartOAuthResponse,
  type ConnectorHealth,
  type HealthStats,
  type AdminMailbox,
  type LogEntry,
  type LogFilters,
  type AuditEvent,
  type AuditFilters,
  type CreateMailboxResult,
  type Tag,
  type TagsResponse,
  type CreateTagInput,
  type BusinessMembersResponse,
  type Contact,
  type ContactsQueryParams,
  type ContactsResponse,
  type ContactResponse,
  type ContactConversationsResponse,
  type CreateContactInput,
  type SearchResponse,
  type Template,
  type TemplatesResponse,
  type CreateTemplateInput,
  type PipelineStage,
  type PipelineStagesResponse,
  type CreatePipelineStageInput,
  type UpdatePipelineStageInput,
  type ReorderPipelineStagesInput,
  type ActionCode,
  type ActionCodesResponse,
  type CreateActionCodeInput,
  type UpdateActionCodeInput,
  type ReorderActionCodesInput,
  type ContactStageHistoryResponse,
  type MoveContactStageResponse,
  type Task,
  type TasksParams,
  type TasksResponse,
  type ScopedTasksResponse,
  type CreateTaskInput,
  type UpdateTaskInput,
  type MeWorkResponse,
  type SyncStatusResponse,
  type ContactNote,
  type ContactNotesResponse,
  type CreateContactNoteInput,
  type UpdateContactNoteInput,
  type ConversationAction,
  type ConversationActionsResponse,
  type CreateConversationActionInput,
  type ConversationExtractionsResponse,
  type EnrichConversationResponse,
} from "@/types/api";

// ---------------------------------------------------------------------------
// Core fetch helper
// ---------------------------------------------------------------------------

/**
 * Fetch a Worker API endpoint with the current user's auth token.
 *
 * This is the single fetch wrapper for ALL API calls (inbox, conversations,
 * admin endpoints, etc.).
 *
 * @param path   - API path, e.g. "/api/inbox?business_id=..."
 * @param init   - Standard RequestInit (method, body, headers, etc.)
 * @returns        The parsed JSON body.
 * @throws         `ApiError` on non-2xx responses.
 * @throws         `SyncCooldownError` on 429 sync_cooldown responses.
 */
export async function fetchApi<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = await getAccessToken();

  const headers = new Headers(init.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });

  const requestId = res.headers.get("x-request-id");

  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const body = await res.json();

      // Handle sync cooldown (429)
      if (res.status === 429 && body?.error === "sync_cooldown") {
        throw new SyncCooldownError(body?.retry_after ?? 60);
      }

      if (body && typeof body === "object" && "error" in body) {
        message =
          typeof body.error === "string"
            ? body.error
            : body.error?.message ?? message;
      }
    } catch (e) {
      if (e instanceof SyncCooldownError) throw e;
      // Response body wasn't JSON — use status text.
      message = res.statusText || message;
    }
    throw new ApiError(message, res.status, requestId);
  }

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Me (user + businesses with roles)
// ---------------------------------------------------------------------------

/**
 * Fetch the current user's profile and business memberships.
 */
export async function getMe(): Promise<MeResponse> {
  return fetchApi<MeResponse>("/api/me");
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

/**
 * Fetch the inbox (conversation list).
 *
 * @param businessId - The business UUID, or undefined for "All Businesses" mode
 *                     (returns conversations across every business the user
 *                     belongs to — the worker's M7 behaviour).
 * @param params     - Optional filters and pagination.
 */
export async function getInbox(
  businessId: string | undefined,
  params: InboxParams = {},
): Promise<InboxResponse> {
  const sp = new URLSearchParams();
  if (businessId) sp.set("business_id", businessId);

  if (params.status) sp.set("status", params.status);
  if (params.mailbox_id) sp.set("mailbox_id", params.mailbox_id);
  if (params.q) sp.set("q", params.q);
  if (params.from_email) sp.set("from_email", params.from_email);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit != null) sp.set("limit", String(params.limit));

  return fetchApi<InboxResponse>(`/api/inbox?${sp.toString()}`);
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

/**
 * Fetch a single conversation by ID.
 */
export async function getConversation(
  id: string,
): Promise<ConversationResponse> {
  return fetchApi<ConversationResponse>(`/api/conversations/${id}`);
}

/**
 * Update a conversation (currently supports status changes only).
 */
export async function updateConversation(
  id: string,
  data: ConversationUpdateBody,
): Promise<ConversationResponse> {
  return fetchApi<ConversationResponse>(`/api/conversations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * Fetch messages for a conversation, ordered oldest-first.
 *
 * @param conversationId - The conversation UUID.
 * @param params         - Optional pagination (limit, cursor).
 */
export async function getMessages(
  conversationId: string,
  params: MessagesParams = {},
): Promise<MessagesResponse> {
  const sp = new URLSearchParams();

  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit != null) sp.set("limit", String(params.limit));

  const qs = sp.toString();
  const path = `/api/conversations/${conversationId}/messages${qs ? `?${qs}` : ""}`;

  return fetchApi<MessagesResponse>(path);
}

// ---------------------------------------------------------------------------
// Businesses
// ---------------------------------------------------------------------------

/**
 * Fetch businesses the current user is a member of.
 */
export async function getBusinesses(): Promise<BusinessesResponse> {
  return fetchApi<BusinessesResponse>("/api/businesses");
}

/**
 * Fetch Google connections for a business.
 */
export async function getBusinessConnections(
  businessId: string,
): Promise<BusinessConnectionsResponse> {
  return fetchApi<BusinessConnectionsResponse>(
    `/api/businesses/${businessId}/connections`,
  );
}

/**
 * Create a new mailbox for a business.
 */
export async function createMailbox(
  body: CreateMailboxBody,
): Promise<CreateMailboxResponse> {
  return fetchApi<CreateMailboxResponse>("/api/admin/mailboxes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------------------

/**
 * Fetch the active draft (status='draft') for a conversation.
 * Returns null when no draft exists — 404 is handled here so callers
 * don't surface an "error" to the console for an expected empty state.
 */
export async function getDraft(conversationId: string): Promise<Draft | null> {
  try {
    return await fetchApi<Draft>(`/api/conversations/${conversationId}/draft`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Create or update a draft.
 */
export async function saveDraft(
  input: SaveDraftInput,
): Promise<SaveDraftResponse> {
  return fetchApi<SaveDraftResponse>("/api/drafts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Approve and send a draft.
 */
export async function sendDraft(
  draftId: string,
): Promise<SendDraftResponse> {
  return fetchApi<SendDraftResponse>(
    `/api/drafts/${draftId}/approve-and-send`,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------------------
// OAuth
// ---------------------------------------------------------------------------

/**
 * Start the Google OAuth flow. Returns the auth URL for redirect.
 */
export async function startOAuth(
  businessId: string,
): Promise<StartOAuthResponse> {
  return fetchApi<StartOAuthResponse>("/api/oauth/start", {
    method: "POST",
    body: JSON.stringify({ business_id: businessId }),
  });
}

/**
 * Fetch a connector's health/status.
 */
export async function getConnectorHealth(
  connectionId: string,
): Promise<{ connector: ConnectorHealth }> {
  return fetchApi<{ connector: ConnectorHealth }>(
    `/api/connectors/${connectionId}`,
  );
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

/**
 * Fetch system health stats.
 */
export async function getHealth(): Promise<{ health: HealthStats }> {
  return fetchApi<{ health: HealthStats }>("/api/admin/health");
}

/**
 * Fetch the list of all mailboxes with sync state.
 */
export async function getAdminMailboxes(): Promise<{
  mailboxes: AdminMailbox[];
}> {
  return fetchApi<{ mailboxes: AdminMailbox[] }>("/api/admin/mailboxes");
}

/**
 * Toggle a mailbox's active status.
 */
export async function toggleMailbox(
  mailboxId: string,
  isActive: boolean,
): Promise<{ ok: boolean; mailbox_id: string; is_active: boolean }> {
  return fetchApi<{ ok: boolean; mailbox_id: string; is_active: boolean }>(
    `/api/admin/mailboxes/${mailboxId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ is_active: isActive }),
    },
  );
}

/**
 * Response shape from DELETE /api/admin/mailboxes/:id.
 *
 * Pre-delete counts are returned so the UI can confirm "N conversations
 * removed"; the Gmail stop + DO destroy outcomes are surfaced for diagnostics
 * (they're best-effort, so a non-success value isn't a hard failure).
 */
export interface DeleteAdminMailboxResponse {
  ok: true;
  mailbox_id: string;
  deleted: {
    conversations: number;
    messages: number;
    attachments: number;
  };
  gmail_stop_outcome: "stopped" | "already_gone" | "failed" | "skipped";
  do_destroy_outcome: "destroyed" | "failed";
}

/**
 * Permanently delete a mailbox + its conversations, messages, attachments,
 * sync state, and drafts (DB cascade). Also best-effort stops the Gmail
 * push watch and destroys the per-mailbox Durable Object so no stale sync
 * ticks fire after.
 */
export async function deleteAdminMailbox(
  mailboxId: string,
): Promise<DeleteAdminMailboxResponse> {
  return fetchApi<DeleteAdminMailboxResponse>(
    `/api/admin/mailboxes/${mailboxId}`,
    { method: "DELETE" },
  );
}

/**
 * Trigger a manual sync (incremental) for a mailbox.
 */
export async function runSync(
  mailboxId: string,
  backfillDays?: number,
): Promise<{
  ok: boolean;
  sync_run_id: string;
  mailbox_id: string;
  message: string;
}> {
  return fetchApi<{
    ok: boolean;
    sync_run_id: string;
    mailbox_id: string;
    message: string;
  }>("/api/admin/sync/run", {
    method: "POST",
    body: JSON.stringify({
      mailbox_id: mailboxId,
      ...(backfillDays != null ? { backfill_days: backfillDays } : {}),
    }),
  });
}

/**
 * Trigger a backfill sync for a mailbox.
 */
export async function backfillMailbox(
  mailboxId: string,
  days?: number,
): Promise<{
  ok: boolean;
  mailbox_id: string;
  days: number;
  message: string;
}> {
  return fetchApi<{
    ok: boolean;
    mailbox_id: string;
    days: number;
    message: string;
  }>(`/api/admin/mailboxes/${mailboxId}/backfill`, {
    method: "POST",
    body: JSON.stringify({ days: days ?? 30 }),
  });
}

/**
 * Fetch event_logs with filters.
 */
export async function getLogs(
  filters: LogFilters,
): Promise<{ logs: LogEntry[] }> {
  const params = new URLSearchParams();
  if (filters.correlation_id)
    params.set("correlation_id", filters.correlation_id);
  if (filters.request_id) params.set("request_id", filters.request_id);
  if (filters.event_name) params.set("event_name", filters.event_name);
  if (filters.mailbox_id) params.set("mailbox_id", filters.mailbox_id);
  if (filters.level) params.set("level", filters.level);
  if (filters.since) params.set("since", filters.since);
  if (filters.limit) params.set("limit", String(filters.limit));

  const qs = params.toString();
  const path = `/api/admin/logs${qs ? `?${qs}` : ""}`;

  return fetchApi<{ logs: LogEntry[] }>(path);
}

/**
 * Fetch audit_events with filters.
 */
export async function getAuditEvents(
  filters: AuditFilters,
): Promise<{ events: AuditEvent[] }> {
  const params = new URLSearchParams();
  if (filters.business_id) params.set("business_id", filters.business_id);
  if (filters.event_type) params.set("event_type", filters.event_type);
  if (filters.since) params.set("since", filters.since);
  if (filters.limit) params.set("limit", String(filters.limit));

  const qs = params.toString();
  const path = `/api/admin/audit${qs ? `?${qs}` : ""}`;

  return fetchApi<{ events: AuditEvent[] }>(path);
}

/**
 * Reset the sync cursor for a mailbox.
 */
export async function resetSyncCursor(
  mailboxId: string,
): Promise<{
  ok: boolean;
  mailbox_id: string;
  previous_cursor: string | null;
  previous_status: string;
  message: string;
}> {
  return fetchApi<{
    ok: boolean;
    mailbox_id: string;
    previous_cursor: string | null;
    previous_status: string;
    message: string;
  }>("/api/admin/sync/reset", {
    method: "POST",
    body: JSON.stringify({ mailbox_id: mailboxId }),
  });
}

/**
 * Create a new admin mailbox.
 */
export async function createAdminMailbox(input: {
  businessId: string;
  googleConnectionId: string;
  email: string;
}): Promise<CreateMailboxResult> {
  return fetchApi<CreateMailboxResult>("/api/admin/mailboxes", {
    method: "POST",
    body: JSON.stringify({
      business_id: input.businessId,
      google_connection_id: input.googleConnectionId,
      email: input.email,
    }),
  });
}

// ---------------------------------------------------------------------------
// Business updates (CRM Phase 3)
// ---------------------------------------------------------------------------

/**
 * Update a business (name, color, etc.).
 */
export async function updateBusiness(
  businessId: string,
  data: BusinessUpdateBody,
): Promise<BusinessUpdateResponse> {
  return fetchApi<BusinessUpdateResponse>(`/api/businesses/${businessId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

/**
 * Response shape from DELETE /api/businesses/:id. Pre-delete counts are
 * returned so the UI can surface "removed N conversations / M members"
 * toast. `teardown_mailbox_failures` is a diagnostic — nonzero means some
 * Gmail watches or R2 objects were left orphaned (DB is still correct).
 */
export interface DeleteBusinessResponse {
  ok: true;
  business_id: string;
  deleted: {
    mailboxes: number;
    conversations: number;
    messages: number;
    attachments: number;
    members: number;
  };
  teardown_mailbox_failures: number;
}

/**
 * Permanently delete a business and everything it owns (mailboxes, conversations,
 * messages, drafts, attachments, memberships, contacts, tags, pipeline stages,
 * tasks, notes, templates). Also stops the Gmail push-watch and destroys the
 * per-mailbox Durable Object for every mailbox in the business. Owner-only.
 */
export async function deleteBusiness(
  businessId: string,
): Promise<DeleteBusinessResponse> {
  return fetchApi<DeleteBusinessResponse>(
    `/api/businesses/${businessId}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Tags (CRM Phase 3)
// ---------------------------------------------------------------------------

/**
 * Fetch all tags for a business.
 */
export async function getTags(businessId: string): Promise<TagsResponse> {
  return fetchApi<TagsResponse>(`/api/tags?business_id=${businessId}`);
}

/**
 * Create a new tag.
 */
export async function createTag(input: CreateTagInput): Promise<{ tag: Tag }> {
  return fetchApi<{ tag: Tag }>("/api/tags", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Delete a tag.
 */
export async function deleteTag(tagId: string): Promise<{ ok: boolean }> {
  return fetchApi<{ ok: boolean }>(`/api/tags/${tagId}`, {
    method: "DELETE",
  });
}

/**
 * Add a tag to a conversation.
 */
export async function addConversationTag(
  conversationId: string,
  tagId: string,
): Promise<{ ok: boolean }> {
  return fetchApi<{ ok: boolean }>(
    `/api/conversations/${conversationId}/tags`,
    {
      method: "POST",
      body: JSON.stringify({ tag_id: tagId }),
    },
  );
}

/**
 * Remove a tag from a conversation.
 */
export async function removeConversationTag(
  conversationId: string,
  tagId: string,
): Promise<{ ok: boolean }> {
  return fetchApi<{ ok: boolean }>(
    `/api/conversations/${conversationId}/tags/${tagId}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Conversation watchers (T1.3)
// ---------------------------------------------------------------------------

/**
 * Add a watcher to a conversation. Idempotent server-side — re-adding an
 * existing watcher returns `{ success: true, already_watching: true }`.
 */
export async function addConversationWatcher(
  conversationId: string,
  userId: string,
): Promise<{ success: boolean; already_watching?: boolean }> {
  return fetchApi<{ success: boolean; already_watching?: boolean }>(
    `/api/conversations/${conversationId}/watchers`,
    {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    },
  );
}

/**
 * Remove a watcher from a conversation. Idempotent — removing a non-watcher
 * returns success.
 */
export async function removeConversationWatcher(
  conversationId: string,
  userId: string,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(
    `/api/conversations/${conversationId}/watchers/${userId}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Business members (CRM Phase 3 - for assignment picker)
// ---------------------------------------------------------------------------

/**
 * Fetch members of a business (for assignment picker).
 */
export async function getBusinessMembers(
  businessId: string,
): Promise<BusinessMembersResponse> {
  return fetchApi<BusinessMembersResponse>(
    `/api/businesses/${businessId}/members`,
  );
}

/** Add a user to a business — creates an auth.users row if email is new. */
export async function addBusinessMember(
  businessId: string,
  input: import("@/types/api").AddMemberInput,
): Promise<import("@/types/api").AddMemberResponse> {
  return fetchApi(`/api/businesses/${businessId}/members`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Update a member's role or display name. */
export async function updateBusinessMember(
  businessId: string,
  userId: string,
  input: import("@/types/api").UpdateMemberInput,
): Promise<import("@/types/api").UpdateMemberResponse> {
  return fetchApi(`/api/businesses/${businessId}/members/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

/** Remove a member — unassigns their open work in this business. */
export async function removeBusinessMember(
  businessId: string,
  userId: string,
): Promise<import("@/types/api").RemoveMemberResponse> {
  return fetchApi(`/api/businesses/${businessId}/members/${userId}`, {
    method: "DELETE",
  });
}

/** Load the centralized team grid: the caller's manageable businesses + members. */
export async function getTeam(): Promise<import("@/types/api").TeamResponse> {
  return fetchApi(`/api/team`);
}

// ---------------------------------------------------------------------------
// Contacts (CRM Phase 3)
// ---------------------------------------------------------------------------

/**
 * Fetch contacts with aggregate + filter support.
 *
 * - Omit `business_id` for the aggregate view across every business the
 *   caller belongs to.
 * - `business_ids` narrows the aggregate to a subset.
 * - `q`, `assigned_to`, `activity` apply on top.
 */
export async function getContacts(
  params: ContactsQueryParams = {},
): Promise<ContactsResponse> {
  const sp = new URLSearchParams();
  if (params.business_id) sp.set("business_id", params.business_id);
  if (params.business_ids && params.business_ids.length > 0) {
    sp.set("business_ids", params.business_ids.join(","));
  }
  if (params.q) sp.set("q", params.q);
  if (params.assigned_to) sp.set("assigned_to", params.assigned_to);
  if (params.activity) sp.set("activity", params.activity);
  if (params.limit != null) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return fetchApi<ContactsResponse>(
    `/api/contacts${qs ? `?${qs}` : ""}`,
  );
}

/**
 * Fetch a single contact by ID. Response includes conversation_count.
 */
export async function getContact(id: string): Promise<ContactResponse> {
  return fetchApi<ContactResponse>(`/api/contacts/${id}`);
}

/**
 * Fetch conversations linked to a contact, newest-first.
 */
export async function getContactConversations(
  id: string,
  params: { limit?: number; cursor?: string } = {},
): Promise<ContactConversationsResponse> {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.cursor) sp.set('cursor', params.cursor);
  const qs = sp.toString();
  return fetchApi<ContactConversationsResponse>(
    `/api/contacts/${id}/conversations${qs ? `?${qs}` : ''}`,
  );
}

/**
 * Create a new contact.
 */
export async function createContact(
  input: CreateContactInput,
): Promise<{ contact: Contact }> {
  return fetchApi<{ contact: Contact }>("/api/contacts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Update a contact.
 */
export async function updateContact(
  id: string,
  data: Partial<Omit<Contact, "id" | "business_id" | "metadata" | "created_at" | "updated_at">>,
): Promise<{ contact: Contact }> {
  return fetchApi<{ contact: Contact }>(`/api/contacts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

/**
 * Delete a contact.
 */
export async function deleteContact(
  id: string,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/api/contacts/${id}`, {
    method: "DELETE",
  });
}

/**
 * Session 21: unified contact-activity timeline.
 */
export async function getContactActivity(
  id: string,
  params: { limit?: number; since?: string } = {},
): Promise<import("@/types/api").ContactActivityResponse> {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.since) sp.set("since", params.since);
  const qs = sp.toString();
  return fetchApi<import("@/types/api").ContactActivityResponse>(
    `/api/contacts/${id}/activity${qs ? `?${qs}` : ""}`,
  );
}

/**
 * Session 21: per-kind extractions summary across all linked conversations.
 */
export async function getContactExtractionsSummary(
  id: string,
): Promise<import("@/types/api").ContactExtractionsSummaryResponse> {
  return fetchApi<import("@/types/api").ContactExtractionsSummaryResponse>(
    `/api/contacts/${id}/extractions-summary`,
  );
}

// ---------------------------------------------------------------------------
// Pipeline stages (T2.1)
// ---------------------------------------------------------------------------

/** Fetch ordered stages for a business. */
export async function getPipelineStages(
  businessId: string,
): Promise<PipelineStagesResponse> {
  return fetchApi<PipelineStagesResponse>(
    `/api/pipeline-stages?business_id=${businessId}`,
  );
}

/** Create a new stage. Server appends to end if `position` is omitted/0. */
export async function createPipelineStage(
  input: CreatePipelineStageInput,
): Promise<{ stage: PipelineStage }> {
  return fetchApi<{ stage: PipelineStage }>("/api/pipeline-stages", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Update a stage's name/color/position. */
export async function updatePipelineStage(
  id: string,
  input: UpdatePipelineStageInput,
): Promise<{ stage: PipelineStage }> {
  return fetchApi<{ stage: PipelineStage }>(`/api/pipeline-stages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

/** Delete a stage. Contacts in this stage have current_stage_id cleared. */
export async function deletePipelineStage(
  id: string,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/api/pipeline-stages/${id}`, {
    method: "DELETE",
  });
}

/** Bulk-reorder stages. Each entry must belong to `business_id`. */
export async function reorderPipelineStages(
  input: ReorderPipelineStagesInput,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>("/api/pipeline-stages/reorder", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// Conversation action codes (migration 0039)
// ---------------------------------------------------------------------------

/** Fetch the ordered "Log action" vocabulary for a business. */
export async function getActionCodes(
  businessId: string,
): Promise<ActionCodesResponse> {
  return fetchApi<ActionCodesResponse>(
    `/api/action-codes?business_id=${businessId}`,
  );
}

/** Create an action code. Server appends to end if `position` is omitted/0. */
export async function createActionCode(
  input: CreateActionCodeInput,
): Promise<{ code: ActionCode }> {
  return fetchApi<{ code: ActionCode }>("/api/action-codes", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Update an action code's code/label/default_channel/position. */
export async function updateActionCode(
  id: string,
  input: UpdateActionCodeInput,
): Promise<{ code: ActionCode }> {
  return fetchApi<{ code: ActionCode }>(`/api/action-codes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

/** Delete an action code. Historical logged actions keep their raw token. */
export async function deleteActionCode(
  id: string,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/api/action-codes/${id}`, {
    method: "DELETE",
  });
}

/** Bulk-reorder action codes. Each entry must belong to `business_id`. */
export async function reorderActionCodes(
  input: ReorderActionCodesInput,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>("/api/action-codes/reorder", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Move a contact to a new stage. Pass `stage_id: null` to remove from
 * pipeline. Inserts a contact_stage_transitions row server-side.
 */
export async function moveContactStage(
  contactId: string,
  stageId: string | null,
): Promise<MoveContactStageResponse> {
  return fetchApi<MoveContactStageResponse>(
    `/api/contacts/${contactId}/stage`,
    {
      method: "PATCH",
      body: JSON.stringify({ stage_id: stageId }),
    },
  );
}

/** Fetch the stage transition history for a contact, newest-first. */
export async function getContactStageHistory(
  contactId: string,
): Promise<ContactStageHistoryResponse> {
  return fetchApi<ContactStageHistoryResponse>(
    `/api/contacts/${contactId}/stage-history`,
  );
}

// ---------------------------------------------------------------------------
// Tasks (T3)
// ---------------------------------------------------------------------------

/**
 * List tasks, paginated. Every filter is optional. When `business_id` is
 * omitted, returns tasks across all businesses the user belongs to.
 */
export async function getTasks(params: TasksParams = {}): Promise<TasksResponse> {
  const sp = new URLSearchParams();
  if (params.business_id) sp.set("business_id", params.business_id);
  if (params.owner_user_id) sp.set("owner_user_id", params.owner_user_id);
  if (params.status) sp.set("status", params.status);
  if (params.contact_id) sp.set("contact_id", params.contact_id);
  if (params.conversation_id) sp.set("conversation_id", params.conversation_id);
  if (params.due_before) sp.set("due_before", params.due_before);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit != null) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return fetchApi<TasksResponse>(`/api/tasks${qs ? `?${qs}` : ""}`);
}

/** Fetch tasks linked to a contact (open first, done within 30d). */
export async function getContactTasks(
  contactId: string,
): Promise<ScopedTasksResponse> {
  return fetchApi<ScopedTasksResponse>(`/api/contacts/${contactId}/tasks`);
}

/** Fetch tasks linked to a conversation. */
export async function getConversationTasks(
  conversationId: string,
): Promise<ScopedTasksResponse> {
  return fetchApi<ScopedTasksResponse>(
    `/api/conversations/${conversationId}/tasks`,
  );
}

/** Create a task. Server fills `created_by` from auth context. */
export async function createTask(
  input: CreateTaskInput,
): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>("/api/tasks", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Update a task. Server stamps / clears completed_at on status transitions. */
export async function updateTask(
  id: string,
  input: UpdateTaskInput,
): Promise<{ task: Task }> {
  return fetchApi<{ task: Task }>(`/api/tasks/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

/** Delete a task. Allowed for admin+ OR the task's creator. */
export async function deleteTask(id: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/api/tasks/${id}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// My work (T3 aggregation)
// ---------------------------------------------------------------------------

/**
 * Aggregated "work" view: assigned conversations + tasks bucketed by due_at
 * + optionally watched conversations. Cross-business when `businessId` is
 * omitted. Pass `includeWatching=true` to include watched_conversations.
 */
export async function getMeWork(
  opts: { businessId?: string; includeWatching?: boolean } = {},
): Promise<MeWorkResponse> {
  const sp = new URLSearchParams();
  if (opts.businessId) sp.set("business_id", opts.businessId);
  if (opts.includeWatching) sp.set("include_watching", "true");
  const qs = sp.toString();
  return fetchApi<MeWorkResponse>(`/api/me/work${qs ? `?${qs}` : ""}`);
}

/**
 * GET /api/me/sync-status — businesses with active mailbox sync issues.
 * Agent+ visible. Empty `businesses` array = everything healthy.
 */
export async function getMySyncStatus(): Promise<SyncStatusResponse> {
  return fetchApi<SyncStatusResponse>("/api/me/sync-status");
}

// ---------------------------------------------------------------------------
// Contact notes (T4)
// ---------------------------------------------------------------------------

/** Fetch notes for a contact, newest-first, capped at 500 server-side. */
export async function getContactNotes(
  contactId: string,
): Promise<ContactNotesResponse> {
  return fetchApi<ContactNotesResponse>(`/api/contacts/${contactId}/notes`);
}

/** Append a note to a contact. `author_user_id` filled server-side from auth. */
export async function createContactNote(
  contactId: string,
  input: CreateContactNoteInput,
): Promise<{ note: ContactNote }> {
  return fetchApi<{ note: ContactNote }>(`/api/contacts/${contactId}/notes`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Update a note. Server enforces author-or-admin. */
export async function updateContactNote(
  noteId: string,
  input: UpdateContactNoteInput,
): Promise<{ note: ContactNote }> {
  return fetchApi<{ note: ContactNote }>(`/api/contact-notes/${noteId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

/** Delete a note. Server enforces author-or-admin. */
export async function deleteContactNote(
  noteId: string,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/api/contact-notes/${noteId}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Conversation actions (SOP gap 2)
// ---------------------------------------------------------------------------

/** List timeline actions for a conversation, ascending by `at`. */
export async function getConversationActions(
  conversationId: string,
): Promise<ConversationActionsResponse> {
  return fetchApi<ConversationActionsResponse>(
    `/api/conversations/${conversationId}/actions`,
  );
}

/** Log a timeline action on a conversation. */
export async function createConversationAction(
  conversationId: string,
  input: CreateConversationActionInput,
): Promise<{ action: ConversationAction }> {
  return fetchApi<{ action: ConversationAction }>(
    `/api/conversations/${conversationId}/actions`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

/** Delete a timeline action. Server enforces author-or-admin. */
export async function deleteConversationAction(
  conversationId: string,
  actionId: string,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(
    `/api/conversations/${conversationId}/actions/${actionId}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Templates (CRM Phase 3)
// ---------------------------------------------------------------------------

/**
 * Fetch all templates for a business.
 */
export async function getTemplates(
  businessId: string,
): Promise<TemplatesResponse> {
  return fetchApi<TemplatesResponse>(
    `/api/templates?business_id=${businessId}`,
  );
}

/**
 * Create a new template.
 */
export async function createTemplate(
  input: CreateTemplateInput,
): Promise<{ template: Template }> {
  return fetchApi<{ template: Template }>("/api/templates", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Update a template.
 */
export async function updateTemplate(
  id: string,
  data: Partial<Pick<Template, "name" | "subject" | "body_plain" | "body_html" | "category" | "variables">>,
): Promise<{ template: Template }> {
  return fetchApi<{ template: Template }>(`/api/templates/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/**
 * Delete a template.
 */
export async function deleteTemplate(
  id: string,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/api/templates/${id}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Search (Full-Text Search)
// ---------------------------------------------------------------------------

/**
 * Full-text search across messages in a business.
 *
 * @param businessId - The business UUID to search within.
 * @param query      - The search query string.
 * @param limit      - Max results (default 20, max 100).
 */
export async function searchMessages(
  businessId: string,
  query: string,
  limit?: number,
): Promise<SearchResponse> {
  const sp = new URLSearchParams();
  sp.set("business_id", businessId);
  sp.set("q", query);
  if (limit != null) sp.set("limit", String(limit));

  return fetchApi<SearchResponse>(`/api/search?${sp.toString()}`);
}

// ---------------------------------------------------------------------------
// Phase 2 — conversation extractions
// ---------------------------------------------------------------------------

/**
 * Fetch structured extractions (phone, address, instructor name, etc.) for
 * a conversation. Triggers a lazy backfill on the worker side for any
 * inbound message that hasn't been extracted yet — first hit is slightly
 * slower, subsequent reads are cached.
 */
export async function getConversationExtractions(
  conversationId: string,
): Promise<ConversationExtractionsResponse> {
  return fetchApi<ConversationExtractionsResponse>(
    `/api/conversations/${conversationId}/extractions`,
  );
}

// ---------------------------------------------------------------------------
// Phase 3 — DeepSeek enrich
// ---------------------------------------------------------------------------

/**
 * Force a DeepSeek LLM enrichment pass on the conversation. Returns the
 * updated extractions plus cost meta (spent / cap). 402 when the per-
 * business cap is exceeded; 502 when DeepSeek fails — both come back as
 * `ApiError`.
 */
export async function enrichConversation(
  conversationId: string,
): Promise<EnrichConversationResponse> {
  return fetchApi<EnrichConversationResponse>(
    `/api/conversations/${conversationId}/enrich`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}
